import { useEffect } from "react";
import { getTenantPreviewSlug } from "../utils/tenantPreview";
import { applyTenantTheme } from "../utils/tenantTheme";
import { usePublicConfig } from "./usePublicConfig";

const DEFAULT_PLATFORM_LOGO_URL = "/uniflo-reference/logo.png";

// Defaults — tenant branding remains DB-driven, while the platform mark
// keeps a stable product fallback for shared chrome such as the workspace rail.
const DEFAULTS = {
  faviconUrl: "",
  touchIconUrl: "",
  siteName: "Uniflo",
  shortDescription: "",
  logoUrl: "",
  platformLogoUrl: DEFAULT_PLATFORM_LOGO_URL,
};

/**
 * Fetch public branding config and apply favicon / touch icon / title
 * to the document head at runtime.
 */
export function useBranding() {
  const isPlatformSurface =
    typeof window !== "undefined" &&
    (window.location.pathname === "/app" ||
      window.location.pathname.startsWith("/app/"));
  const tenantPreviewSlug = getTenantPreviewSlug();

  const { data } = usePublicConfig();

  const branding = data?.branding || {};
  const platformBranding = data?.platformBranding || {};
  const general = data?.general || {};
  const tenant = isPlatformSurface ? null : data?.tenant || null;
  const appearance = isPlatformSurface ? null : data?.appearance || null;

  // Apply tenant appearance colors as CSS custom properties so Primary /
  // Secondary / Tertiary / Other drive the look of the public site and the
  // signed-in clan UI. When a field is empty we fall back to the Uniflo
  // default palette from uniflo-brand.css.
  useEffect(() => {
    if (isPlatformSurface) {
      applyTenantTheme({});
      return;
    }
    applyTenantTheme(appearance || {});
  }, [
    isPlatformSurface,
    appearance?.primaryColor,
    appearance?.secondaryColor,
    appearance?.tertiaryColor,
    appearance?.otherColor,
    appearance?.linkColor,
  ]);

  const faviconUrl =
    (isPlatformSurface ? platformBranding.faviconUrl : branding.faviconUrl) ||
    branding.faviconUrl ||
    platformBranding.faviconUrl ||
    DEFAULTS.faviconUrl;
  const touchIconUrl =
    (isPlatformSurface ? platformBranding.touchIconUrl : branding.touchIconUrl) ||
    branding.touchIconUrl ||
    platformBranding.touchIconUrl ||
    DEFAULTS.touchIconUrl;
  const siteName =
    (isPlatformSurface ? platformBranding.siteName : branding.siteName) ||
    branding.siteName ||
    general.siteName ||
    platformBranding.siteName ||
    (isPlatformSurface ? "Uniflo Platform" : DEFAULTS.siteName);
  const shortDescription =
    (isPlatformSurface ? "" : branding.shortDescription) ||
    branding.shortDescription ||
    general.siteDescription ||
    (isPlatformSurface
      ? "Operate the Uniflo platform, tenants, subscriptions, and package catalog."
      : DEFAULTS.shortDescription);
  const logoUrl =
    (isPlatformSurface ? platformBranding.logoUrl : branding.logoUrl) ||
    branding.logoUrl ||
    platformBranding.logoUrl ||
    DEFAULTS.logoUrl;
  const platformSiteName = platformBranding.siteName || DEFAULTS.siteName;
  const platformDomain = platformBranding.platformDomain || "uniflo.app";
  const platformSupportEmail = platformBranding.supportEmail || "support@uniflo.app";
  const platformSupportPhone = platformBranding.supportPhone || "";
  const platformFaviconUrl = platformBranding.faviconUrl || DEFAULTS.faviconUrl;
  const platformTouchIconUrl = platformBranding.touchIconUrl || DEFAULTS.touchIconUrl;
  const platformLogoUrl =
    platformBranding.logoUrl || DEFAULTS.platformLogoUrl;

  // Update <link rel="icon">
  useEffect(() => {
    if (!faviconUrl) return;
    const el =
      document.querySelector('link[rel="icon"]') ||
      document.querySelector('link[rel="shortcut icon"]');
    if (el) el.href = faviconUrl;
  }, [faviconUrl]);

  // Update <link rel="apple-touch-icon">
  useEffect(() => {
    if (!touchIconUrl) return;
    const el = document.querySelector('link[rel="apple-touch-icon"]');
    if (el) el.href = touchIconUrl;
  }, [touchIconUrl]);

  // Update <title>
  useEffect(() => {
    if (siteName) document.title = siteName;
  }, [siteName]);

  // Update <meta name="description">
  useEffect(() => {
    const el = document.querySelector('meta[name="description"]');
    if (el && shortDescription) el.setAttribute("content", shortDescription);
  }, [shortDescription]);

  return {
    faviconUrl,
    touchIconUrl,
    siteName,
    shortDescription,
    logoUrl,
    platformSiteName,
    platformDomain,
    platformSupportEmail,
    platformSupportPhone,
    platformFaviconUrl,
    platformTouchIconUrl,
    platformLogoUrl,
    tenant,
    tenantPreviewSlug,
  };
}
